import {
  CocoaCollectionType,
  CocoaContentType,
  CocoaEntityType,
  CocoaMetaEntityType,
} from '@src/utils/constants.util'

export type Collection = {
  slug: string
  title: string
  subtitle?: string
  items: CocoApi.CollectionItem[]
}

export type MetaCollectionType = {
  imageUrl?: string
  label: string
  slug: string
  maxValue?: number
  minValue?: number
} & Pick<CocoApi.MetaEntity, 'images'>

/** Root-absolute streaming CDP paths (e.g. `/app/blocks/foo`) so Link hrefs never resolve relative to the current URL. */
export const buildContentRedirectPath = (contentBaseRoute: string, slug?: string): string => {
  if (!slug) return ''
  return `/app/${contentBaseRoute}/${slug}`
}

/** Ensures Cocoa-provided paths are root-absolute when used as Link hrefs. */
export const toContentRootPath = (path: string): string => {
  if (path.startsWith('http') || path.startsWith('/')) return path
  return `/${path}`
}

export const parseCollectionData = (
  moduleData?: CocoApi.CollectionModule,
  entities?: CocoApi.Entities
): Collection => {
  const moduleDataList = moduleData?.entityIds?.map((item) => {
    const entity = (entities?.[item] ?? moduleData.entities?.[item]) as CocoApi.CollectionListEntity
    let contentBaseRoute = CocoaContentType.PROGRAMS
    let mobileImage
    let backgroundImage

    if (entity?._type === CocoaEntityType.compilation)
      contentBaseRoute = CocoaContentType.COMPILATIONS
    if (entity?._type === CocoaEntityType.bodiBlock) contentBaseRoute = CocoaContentType.BLOCKS

    let redirectUrl = buildContentRedirectPath(contentBaseRoute, entity?.slug)

    if (moduleData?.collectionType === CocoaCollectionType.compilation) {
      mobileImage = entity?.imageSquare?.url
      backgroundImage = entity?.thumbnailPanoramic?.url
    } else if (moduleData?.collectionType === CocoaCollectionType.bodiBlock) {
      backgroundImage = entity?.thumbnailPortraitWithLogo?.url
    } else if (moduleData?.collectionType === CocoaCollectionType.showcase) {
      backgroundImage = entity?.thumbnailLandscapeWithLogo?.url
    } else if (moduleData?.collectionType === CocoaCollectionType.relatedContent) {
      redirectUrl = entity?.url ? toContentRootPath(entity.url) : redirectUrl
      backgroundImage = entity?.image?.url
    } else {
      backgroundImage = entity?.thumbnail?.url
    }

    return {
      redirectUrl,
      id: entity?._id ?? '',
      slug: entity?.slug ?? '',
      title: entity?.title ?? '',
      type: entity?._type ?? '',
      brandCode: entity?.brandCode,
      logoUrl: entity?.logo?.url,
      backgroundImage,
      mobileImage,
      dateStart: entity?.dateStart,
      description: entity?.description ?? '',
      descriptionShort: entity?.descriptionShort ?? '',
      hasJoined: entity?.userData?.isJoined,
      isEntitled: entity?.userData?.isEntitled ?? false,
      thumbnailLandscapeUrl: entity?.thumbnailLandscape?.url,
      thumbnailPanoramicUrl: entity?.thumbnailPanoramic?.url,
      stamps: entity?.stamps ?? [],
      statistics: [
        entity?.itemCount ?? null,
        entity?.commitment ?? null,
        entity?.level ?? null,
      ].filter((item) => item !== null),
    }
  })

  return {
    slug: moduleData?.slug ?? '',
    title: moduleData?.title ?? '',
    subtitle: moduleData?.subtitle ?? undefined,
    items: moduleDataList as CocoApi.CollectionItem[],
  }
}

export const parseCustomCollectionData = (
  module: CocoApi.CollectionModule,
  entities: CocoApi.Entities
): CocoApi.CustomCollection => {
  const moduleDataList = module.entityIds?.map((item) => {
    const entity = entities[item] as CocoApi.CustomCollectionEntity
    if (!entity) return null

    const type = entity?._type ? CocoaEntityType[entity._type] : CocoaEntityType.program
    let contentBaseRoute = CocoaContentType.PROGRAMS as string

    if (entity?._type === CocoaEntityType.compilation)
      contentBaseRoute = CocoaContentType.COMPILATIONS
    if (entity?._type === CocoaEntityType.bodiBlock) contentBaseRoute = CocoaContentType.BLOCKS

    const redirectUrl = `/app/${contentBaseRoute}/${entity?.slug}`
    let backgroundImage

    if (module.collectionType === CocoaCollectionType.compilation) {
      backgroundImage = entity?.thumbnailPanoramic?.url
    } else if (module.collectionType === CocoaCollectionType.bodiBlock) {
      backgroundImage = entity?.thumbnailPortraitWithLogo?.url
    } else if (module.collectionType === CocoaCollectionType.showcase) {
      backgroundImage = entity?.thumbnailLandscapeWithLogo?.url
    } else if (module.collectionType === CocoaCollectionType.mixed) {
      backgroundImage = entity?.thumbnailLandscapeWithLogo?.url ?? entity?.thumbnail?.url
    } else {
      backgroundImage = entity?.thumbnail?.url
    }

    return {
      id: entity?._id ?? '',
      slug: entity?.slug,
      type: type as CocoApi.EntityType,
      title: entity?.title ?? '',
      logoUrl: entity?.logo?.url,
      dateStart: entity?.dateStart,
      catalog: entity?.catalog,
      containerQuantities: entity?.containerQuantities,
      containerQuantitiesVegan: entity?.containerQuantitiesVegan,
      backgroundImage,
      description: entity?.description ?? '',
      hasJoined: entity?.userData?.isJoined,
      isEntitled: entity?.userData?.isEntitled ?? false,
      isFavoritable: entity?.isFavoritable,
      isPlaceholder: entity?.isPlaceholder,
      dietaryGroups: entity?.dietaryGroups,
      subcategories: entity?.subcategories,
      trainers: entity?.trainers,
      categories: entity?.categories,
      redirectUrl,
      recipeFile: entity?.recipeFile,
      stamps: entity?.stamps ?? [],
      userData: entity?.userData,
      video: entity?.video,
      languageStamp: entity?.languageStamp,
      classData: entity?.classData,
      descriptionLong: entity?.descriptionLong,
      durationEstimate: entity?.durationEstimate,
      equipmentRequired: entity?.equipmentRequired,
      equipmentRecommended: entity?.equipmentRecommended,
      primaryParent: entity?.primaryParent,
      recommendationId: entity?.recommendationId,
      purchaseOptions: entity?.purchaseOptions,
      programStatistics: [
        entity?.duration ?? null,
        entity?.commitment ?? null,
        entity?.level ?? null,
      ].filter((item) => item !== null),
      statistics: [
        entity?.itemCount ?? null,
        entity?.commitment ?? null,
        entity?.level ?? null,
      ].filter((item) => item !== null),
      associatedContent: entity?.associatedContent,
      nutritionPlanBucket: entity?.nutritionPlanBucket,
      levels: entity?.levels,
      _type: entity?._type,
      _id: entity?._id,
      thumbnail: entity?.thumbnail,
    }
  })

  return {
    slug: module.slug ?? '',
    title: module.title ?? '',
    items: moduleDataList?.filter((item) => !!item) as CocoApi.CustomCollectionEntity[],
  }
}

export const parseMetaData = (moduleData?: CocoApi.MetaCollectionModule) => {
  const moduleDataList = moduleData?.entityIds?.map((id) => {
    const entity = moduleData?.entities?.[id]
    let imageUrl
    if (entity?._type === CocoaMetaEntityType.trainer) {
      imageUrl = entity?.images?.cutout?.url ?? entity?.images?.bodiCutout?.url
    } else {
      imageUrl = entity?.image?.url
    }
    return {
      id: entity?._id ?? '',
      slug: entity?.slug ?? '',
      title: entity?.title ?? '',
      details: '', // TODO: update this when we have statistics for trainers (entity.description)
      imageUrl,
    }
  })

  return {
    slug: moduleData?.slug ?? '',
    title: moduleData?.title ?? '',
    subtitle: moduleData?.subtitle ?? '',
    items: moduleDataList ?? [],
  }
}
